import React, { useEffect, useRef, useCallback } from 'react';
import AutoNumeric from 'autonumeric';

/**
 * CurrencyInput - Componente reutilizável para campos monetários brasileiros
 *
 * Usa AutoNumeric.js para formatação automática
 * Configuração: BRL (Real brasileiro)
 */
const CurrencyInput = ({
  value,
  onChange,
  name,
  placeholder = '0,00',
  disabled = false,
  required = false,
  className = '',
  id,
  ...rest
}) => {
  const inputRef = useRef(null);
  const anInstance = useRef(null);

  // Configuração AutoNumeric para Real Brasileiro
  const config = {
    currencySymbol: 'R$ ',
    decimalCharacter: ',',
    decimalCharacterAlternative: '.',
    digitGroupSeparator: '.',
    allowDecimalPadding: true,
    decimals: 2,
    minimumValue: 0,
    maximumValue: '999999999999.99',
    negative: false,
  };

  // Cria instância do AutoNumeric
  useEffect(() => {
    if (!inputRef.current) return;

    try {
      anInstance.current = new AutoNumeric(inputRef.current, config);
    } catch (error) {
      console.error('AutoNumeric initialization error:', error);
    }

    return () => {
      if (anInstance.current) {
        try {
          anInstance.current.remove();
        } catch (e) {
          // Ignora erros no cleanup
        }
        anInstance.current = null;
      }
    };
  }, []);

  // Atualiza o valor quando prop muda
  useEffect(() => {
    if (anInstance.current && value !== undefined && value !== null) {
      try {
        // Só atualiza se o valor for diferente para evitar loop
        const currentValue = anInstance.current.getNumber();
        if (currentValue !== value) {
          anInstance.current.set(value, { rawValue: true });
        }
      } catch (error) {
        console.error('AutoNumeric set value error:', error);
      }
    }
  }, [value]);

  // Handler para onChange - chama o callback com o valor numérico
  const handleChange = useCallback(() => {
    if (anInstance.current && onChange) {
      const numericValue = anInstance.current.getNumber();
      onChange(numericValue || 0);
    }
  }, [onChange]);

  // Handler para onBlur - garantia de que o valor final é capturado
  const handleBlur = useCallback(() => {
    if (anInstance.current && onChange) {
      const numericValue = anInstance.current.getNumber();
      onChange(numericValue || 0);
    }
  }, [onChange]);

  return (
    <input
      ref={inputRef}
      type="text"
      name={name}
      id={id}
      disabled={disabled}
      required={required}
      className={className}
      onChange={handleChange}
      onBlur={handleBlur}
      {...rest}
    />
  );
};

export default CurrencyInput;
